import { useApi } from 'api/api-context';
import useImmediateAction from 'api/use-immediate-action';
import RevisionForm, { RevisionDevice, RevisionFormData } from 'components/forms/revision/RevisionForm';
import panic from 'errors/panic';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { EDIT_FORM_PAGE_PATH, EDIT_REVISION_PAGE_PATH, FORMS_PAGE_PATH, REVISIONS_PAGE_PATH } from 'routes/paths';
import { notifications } from '@mantine/notifications';
import DashboardLayout from 'layouts/dashboard/DashboardLayout';
import { IconCopy } from '@tabler/icons-react';
import { isEmpty } from 'lodash';
import { ERROR_NOTIFICATION_COLOR, WARNING_NOTIFICATION_COLOR } from 'utils/constants';
import { useEffect } from 'react';
import { REVISION_REDIRECT_AFTER_SAVE } from 'env';
import { copyRevisionData } from 'pages/revisions-module/copy-revision/copy-revision-data';

export type RevisionDevices = {
  deviceId: number;
  deviceName: string;
  termId?: number;
  deviceSubtypeId: number;
  deviceSubtypeName: string;
  checked: boolean;
  manufacturer?: string;
  type: string;
  serialNumber?: string;
  manufactured?: number;
  internalPossessionsNumber?: string;
  location?: string;
  organization?: {
    organizationId: number;
    organizationName: string;
  };
}[];

/**
 * Page used to copy a new revision.
 *
 */
export default function CopyRevisionPage() {
  const navigate = useNavigate();
  const { getAction } = useApi();
  const { revisionId: oldRevisionId } = useParams();

  const {
    data: oldRevision,
    loading,
    error,
  } = useImmediateAction(() => {
    const action = getAction('RevisionGet');
    return action({ parameters: { revisionId: oldRevisionId! } });
  });

  useEffect(() => {
    if (oldRevision && oldRevision.devices.some((device) => !device.status)) {
      notifications.show({
        title:
          oldRevision.kind === 'revision'
            ? 'Revíznej správe sme odobrali nejaké zariadenia'
            : 'Formuláru sme odobrali nejaké zariadenia',
        message:
          oldRevision.kind === 'revision'
            ? 'Niektoré zariadenia pôvodnej revíznej správy sú deaktivované.'
            : 'Niektoré zariadenia pôvodného formulára sú deaktivované.',
        color: WARNING_NOTIFICATION_COLOR,
      });
    }
  }, [oldRevision]);

  if (!oldRevisionId) {
    return <Navigate to={REVISIONS_PAGE_PATH.original} />;
  }

  if (error) {
    panic(error);
    return <></>;
  }

  /**
   * Runs when the form is submitted.
   */
  async function onSubmit({
    organizationId,
    departmentId,
    revisionName,
    deadline,
    technicianId,
    devices,
  }: RevisionFormData) {
    try {
      const revisionCreate = getAction('RevisionCreate');
      let newRevisionData;

      const mappedDevices = Object.keys(devices)
        .map((deviceId) => ({
          deviceId: Number(deviceId),
          deviceName: devices[deviceId].deviceName,
          termId:
            devices[deviceId].termId && devices[deviceId].termId !== '' ? Number(devices[deviceId].termId) : undefined,
          deviceSubtypeId: devices[deviceId].deviceSubtypeId,
          deviceSubtypeName: devices[deviceId].deviceSubtypeName,
          checked: devices[deviceId].checked,
          manufacturer: devices[deviceId].manufacturer,
          type: devices[deviceId].type,
          serialNumber: devices[deviceId].serialNumber,
          manufactured: devices[deviceId].manufactured,
          internalPossessionsNumber: devices[deviceId].internalPossessionsNumber,
          location: devices[deviceId].location,
          organization: devices[deviceId].organization,
          building: devices[deviceId].building,
        }))
        .filter((device) => device.checked);

      if (oldRevision && oldRevision.revisionData) {
        const newDeviceIds = Object.keys(devices)
          .filter((deviceId) => devices[deviceId].checked)
          .map(Number);

        newRevisionData = copyRevisionData({ oldRevision, newDeviceIds });
      }

      if (isEmpty(mappedDevices)) {
        notifications.show({
          title:
            oldRevision!.kind === 'revision'
              ? 'Revízna správa nemôže byť skopírovaná'
              : 'Formulár nemôže byť skopírovaný',
          message:
            oldRevision!.kind === 'revision'
              ? 'Všetky zariadenia pôvodnej revíznej správy sú deaktivované.'
              : 'Všetky zariadenia pôvodného formulára sú deaktivované.',
          color: ERROR_NOTIFICATION_COLOR,
        });

        return;
      }

      const copiedRevision = await revisionCreate({
        payload: {
          revisionName,
          deadline: deadline || undefined,
          organizationId: Number(organizationId),
          departmentId: Number(departmentId),
          deviceTypeId: oldRevision!.deviceType.deviceTypeId,
          devices: mappedDevices.map((device) => ({
            deviceId: device.deviceId,
            deviceSubtypeId: device.deviceSubtypeId,
            termId: device.termId,
          })),
          technicianId: technicianId ? Number(technicianId) : undefined,
          revisionData: newRevisionData ? JSON.stringify(newRevisionData) : undefined,
          revisionTemplate: oldRevision!.revisionTemplate?.slug,
          kind: oldRevision!.kind,
        },
      });

      notifications.show({
        title: oldRevision!.kind === 'revision' ? 'Revízna správa bola skopírovaná' : 'Formulár bol skopírovaný',
        message:
          oldRevision!.kind === 'revision'
            ? `Revízna správa ${
                oldRevision!.revisionName
              } bola úspešne skopírovaná a revízna správa ${revisionName} bola úspešne vytvorená.`
            : `Formulár ${
                oldRevision!.revisionName
              } bol úspešne skopírovaný a formulár ${revisionName} bol úspešne vytvorený.`,
        color: 'green',
      });

      if (REVISION_REDIRECT_AFTER_SAVE === 'same') {
        navigate(
          oldRevision!.kind === 'revision'
            ? EDIT_REVISION_PAGE_PATH.insert({ revisionId: copiedRevision.revisionId })
            : EDIT_FORM_PAGE_PATH.insert({ revisionId: copiedRevision.revisionId })
        );
      } else if (REVISION_REDIRECT_AFTER_SAVE === 'list') {
        navigate(oldRevision!.kind === 'revision' ? REVISIONS_PAGE_PATH.original : FORMS_PAGE_PATH.original);
      }
    } catch (error: any) {
      panic(error);
    }
  }

  return (
    <DashboardLayout
      breadcrumbs={[
        {
          title: oldRevision?.kind === 'revision' ? 'Revízne správy' : 'Formuláre',
          link: oldRevision?.kind === 'revision' ? REVISIONS_PAGE_PATH.original : FORMS_PAGE_PATH.original,
        },
        { title: oldRevision?.kind === 'revision' ? 'Skopírovanie revíznej správy' : 'Skopírovanie formulára' },
      ]}
      title={`${oldRevision?.kind === 'revision' ? 'Skopírovanie revíznej správy' : 'Skopírovanie formulára'}: ${
        oldRevision?.revisionName ?? 'Načítavam...'
      }`}
    >
      {!loading && (
        <RevisionForm
          context="edit"
          kind={oldRevision!.kind === 'revision' ? 'revision' : 'form'}
          onSubmit={onSubmit}
          initialValues={{
            subKind: oldRevision.kind,
            revisionName: `${oldRevision.revisionName} (Kópia)`,
            deadline: '',
            organizationId: String(oldRevision.organization.organizationId),
            departmentId: String(oldRevision.department.departmentId),
            deviceTypeId: String(oldRevision.deviceType.deviceTypeId),
            devices: oldRevision.devices.reduce((acc, device) => {
              acc[String(device.deviceId)] = {
                deviceId: device.deviceId,
                deviceName: device.deviceName,
                termId: device.termId ? String(device.termId) : '',
                deviceTypeId: device.deviceType?.deviceTypeId ?? oldRevision.deviceType.deviceTypeId,
                deviceSubtypeId: device.deviceSubtype?.deviceTypeId ?? 0,
                deviceSubtypeName: device.deviceSubtype?.deviceTypeName ?? '',
                checked: device.status,
                manufacturer: device.manufacturer,
                type: device.type,
                serialNumber: device.serialNumber,
                manufactured: device.manufactured,
                internalPossessionsNumber: device.internalPossessionsNumber ?? '',
                location: device.location ?? '',
                building: device.building ?? '',
                parentDevice: undefined, // TODO: Extend API to include parent and child devices if needed
                childDevices: undefined, // TODO: Extend API to include parent and child devices if needed
                structure: device.structure,
                organization: device.organization
                  ? {
                      organizationId: device.organization.organizationId,
                      organizationName: device.organization.organizationName,
                    }
                  : undefined,
                revisionPlan: (device.revisionPlan || []).map((rp) => ({
                  revisionPlanId: rp.revisionPlanId!,
                  description: rp.description,
                  date: rp.date,
                  revisionPeriod: rp.revisionPeriod,
                  neverExecuted: rp.neverExecuted,
                  deviceTypeId: rp.deviceTypeId ?? device.deviceType?.deviceTypeId ?? 0,
                })),
              } satisfies RevisionDevice;

              return acc;
            }, {} as { [deviceId: string]: RevisionDevice }),
            technicianId: oldRevision.technician ? String(oldRevision.technician.userId) : '',
          }}
          primaryButtonText="Skopírovať"
          primaryIcon={<IconCopy stroke={1.5} size={24} />}
          readOnly={{
            deviceTypeId: true,
          }}
        />
      )}
    </DashboardLayout>
  );
}
